import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, Cell } from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';
import { IntegrationBreakdownItem } from '@/apis/agency/businessInsights/types';

const INTEGRATION_BAR_COLORS = [
  '#195EFF',
  '#F06A6A',
  '#2C3E50',
  '#8B5CF6',
  '#F59E0B',
  '#94A3B8',
  '#1E293B',
  '#10B981',
  '#0D9488',
  '#EAB308',
  '#EF4444'
];

const getIntegrationBarColor = (index: number) => INTEGRATION_BAR_COLORS[index % INTEGRATION_BAR_COLORS.length];

interface IntegrationBreakdownTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: IntegrationBreakdownItem }>;
}

function IntegrationBreakdownTooltip({ active, payload }: IntegrationBreakdownTooltipProps) {
  const theme = useTheme();

  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0].payload;

  return (
    <Box
      bgcolor='#fff'
      border='1px solid #ECECEC'
      borderRadius={1.5}
      boxShadow='0 4px 12px rgba(0, 0, 0, 0.08)'
      px={1.5}
      py={1.25}
    >
      <Typography fontWeight={600} fontSize={14} color='#09090A' mb={0.5}>
        {item.name}
      </Typography>
      <Typography fontSize={13} color={theme.palette.primary.main}>
        Active Businesses: {item.active_businesses}
      </Typography>
      <Typography fontSize={13} color='#9CA3AF'>
        Active Connections : {item.active_connections}
      </Typography>
    </Box>
  );
}

interface Props {
  data: IntegrationBreakdownItem[];
}

export default function IntegrationBreakdownChart({ data }: Props) {
  return (
    <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2} mt={3}>
      <Typography variant='h6' fontWeight={500}>
        Active Business Count by Integration
      </Typography>

      <ResponsiveContainer width='100%' height={Math.max(200, data.length * 48)}>
        <BarChart data={data} layout='vertical' margin={{ left: -5, right: 40, top: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          <XAxis type='number' allowDecimals={false} />
          <YAxis type='category' dataKey='name' width={140} tick={{ fontSize: 12 }} />
          <Tooltip content={<IntegrationBreakdownTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
          <Bar dataKey='active_businesses' name='Active Businesses' radius={[0, 11, 11, 0]} barSize={29}>
            {data.map((entry, index) => (
              <Cell key={entry.code || entry.name} fill={getIntegrationBarColor(index)} />
            ))}
            <LabelList dataKey='active_businesses' position='right' style={{ fontSize: 12, fill: '#555' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
